export type PlanTier = 'Lite' | 'Plus' | 'Enterprise';

export interface PlanFeature {
  label: string;
  included: boolean;
}

export interface PlanQuotas {
  docsPerMonth: number | null; // null = no fixed limit
  storageGb: number | null;
}

export interface Plan {
  tier: PlanTier;
  name: string;
  tagline: string;
  priceLabel: string; // display only; real price lives in Stripe
  priceSuffix: string;
  docsLabel: string;
  quotas: PlanQuotas;
  cta: 'checkout' | 'contact';
  highlighted: boolean;
  features: PlanFeature[];
}

export const PLANS: Plan[] = [
  {
    tier: 'Lite',
    name: 'Lite',
    tagline: 'For small businesses',
    priceLabel: '€99',
    priceSuffix: '/mo · per VAT no.',
    docsLabel: '100 docs / month',
    quotas: { docsPerMonth: 100, storageGb: 1 },
    cta: 'checkout',
    highlighted: false,
    features: [
      { label: 'Unlimited users', included: true },
      { label: 'Legal compliance archiving', included: true },
      { label: '1 GB storage included', included: true },
      { label: 'CSV upload + easy mapping tool', included: true },
      { label: 'Manual invoice & credit note creation', included: true },
      { label: 'Support Knowledge Hub', included: true },
      { label: 'Automated daily reports', included: false },
      { label: 'API access', included: false },
      { label: 'AI error handling', included: false }
    ]
  },
  {
    tier: 'Plus',
    name: 'Plus',
    tagline: 'Most popular choice',
    priceLabel: '€169',
    priceSuffix: '/mo · per VAT no.',
    docsLabel: '1,000 docs / month',
    quotas: { docsPerMonth: 1000, storageGb: 2 },
    cta: 'checkout',
    highlighted: true,
    features: [
      { label: 'Everything in Lite', included: true },
      { label: '2 GB storage included', included: true },
      { label: 'Automated daily activity reports', included: true },
      { label: 'API access', included: true },
      { label: 'AI error handling', included: true },
      { label: 'Real-time language translation', included: false },
      { label: 'Access to tax experts', included: false },
      { label: 'SSO + multi-entity management', included: false }
    ]
  },
  {
    tier: 'Enterprise',
    name: 'Enterprise',
    tagline: 'For large organisations',
    priceLabel: 'Custom',
    priceSuffix: '· contact sales',
    docsLabel: 'Unlimited docs / month',
    quotas: { docsPerMonth: null, storageGb: null },
    cta: 'contact',
    highlighted: false,
    features: [
      { label: 'Everything in Plus', included: true },
      { label: 'Custom storage plans', included: true },
      { label: 'Real-time input language translation', included: true },
      { label: 'Access to tax experts', included: true },
      { label: 'Single sign-on (SSO)', included: true },
      { label: 'Enterprise org access (multi-entity)', included: true }
    ]
  }
];

// Free tier is not on the pricing cards: 5 docs / month, 200 MB storage
const FREE_QUOTAS: PlanQuotas = { docsPerMonth: 5, storageGb: 200 / 1024 };

// Usage quotas for an org's plan value; Enterprise (Plus) has no fixed limit
export const getPlanQuotas = (plan?: string | null): PlanQuotas => {
  const tier = plan === 'Enterprise Plus' ? 'Enterprise' : plan;
  if (!tier || tier === 'Free') return FREE_QUOTAS;
  return PLANS.find((p) => p.tier === tier)?.quotas ?? { docsPerMonth: null, storageGb: null };
};
